import {useAuth} from '../auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import {useMenuConditions} from '../admin/utils/use-menu-conditions';
import dot from 'dot-object';
import {useMemo} from 'react';
import {User} from '@ui/types/user';
import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();
  const {checkCondition, shouldHideMenuItem, isMainDomain} =
    useMenuConditions();

  // Check if this is tenant sidebar request IMMEDIATELY (before useMemo)
  const isTenantSidebarRequest = (() => {
    // Only check string match - trust the menuName passed from AdminSidebar
    if (typeof menuOrPosition === 'string') {
      return menuOrPosition === 'tenant-admin-sidebar';
    }

    // If positions includes tenant-admin-sidebar
    if (menuOrPosition?.positions?.includes('tenant-admin-sidebar')) {
      return true;
    }

    return false;
  })();

  // Calculate domain outside useMemo so it can be tracked in dependencies
  const mainDomain = false;
 
  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }


    // ===== TENANT SIDEBAR: Completely separate menu logic =====
    if (isTenantSidebarRequest) {
    
      return buildTenantSidebarMenu();
    }

    // ===== ADMIN & OTHER MENUS: Original logic =====
    let menu: MenuConfig | undefined | null =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    const hasAdminAccess = hasPermission('admin.access');

    const menuItems: MenuItemConfig[] = [];
    const isAuthDropdown = (() => {
      if (typeof menuOrPosition === 'string') {
        return menuOrPosition === 'auth-dropdown';
      }
      return (menuOrPosition?.positions || []).includes('auth-dropdown');
    })();

    if (menu) {
      for (const item of [...menu.items]) {
        const action = item.action || '';

        // Skip Tags/Files only on main admin-sidebar
        if (
          menuOrPosition === 'admin-sidebar' &&
          ['/admin/tags', '/admin/files'].includes(action)
        ) {
          continue;
        }

        // Filter out billing from auth dropdown (all domains)
        if (isAuthDropdown && ['/billing', '/tenant/billing'].includes(action)) {
          continue;
        }

        // Filter out billing from main domain admin sidebar
        if (
          mainDomain &&
          menuOrPosition === 'admin-sidebar' &&
          ['/billing', '/tenant/billing'].includes(action)
        ) {
          continue;
        }

        // Filter out all admin items from auth dropdown on tenant domains
        // EXCEPT keep /admin (Admin Area) link
        if (isAuthDropdown && !mainDomain) {
          if (action.startsWith('/admin') && action !== '/admin') {
            continue;
          }
        }

        // Filter out all admin sub-items from auth dropdown on main domain (keep only /admin)
        if (
          isAuthDropdown &&
          mainDomain &&
          (action.startsWith('/admin') && action !== '/admin')
        ) {
          continue;
        }

        console.log('......'+menuOrPosition)
        // Filter out tenant billing from main domain admin sidebar
        if (
          
         
          menuOrPosition === 'tenant-admin-sidebar' &&
          ['/tenant/billing'].includes(action)
        ) {
        
          continue;
        }

        

        const itemWithLabel: MenuItemConfig = {...item};

        // Rename tenant items on main domain admin sidebar ONLY
        if (mainDomain && menuOrPosition === 'admin-sidebar') {
          if (action === '/admin/plans') {
            itemWithLabel.label = 'Tenant Plans';
          } else if (action === '/admin/subscriptions') {
            itemWithLabel.label = 'Tenant Subscriptions';
          }
        }

        const hasRoles = (itemWithLabel.roles || []).every(
          roleId => user?.roles.find(r => r.id === roleId),
        );
        const hasPermissions = (itemWithLabel.permissions || []).every(p =>
          hasPermission(p),
        );
        const hasSettings = !itemWithLabel.settings ||
          Object.entries(itemWithLabel.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        const conditionPassed =
          !itemWithLabel.condition || checkCondition(itemWithLabel.condition);

        const isHidden = shouldHideMenuItem(
          itemWithLabel,
          typeof menuOrPosition === 'string' ? menuOrPosition : undefined,
        );

        const subscriptionOk = subscriptionStatusMatches(itemWithLabel, user);

        const shouldSkip =
          !itemWithLabel.action ||
          !hasRoles ||
          !hasPermissions ||
          !hasSettings ||
          !conditionPassed ||
          isHidden ||
          !subscriptionOk;

        if (shouldSkip) {
          continue;
        }

        if (user?.id) {
          itemWithLabel.action = itemWithLabel.action.replace(
            /{currentUser}/g,
            `${user.id}`,
          );
        }

        menuItems.push(itemWithLabel);
      }
      menu = {...menu, items: menuItems};
    }

    return menu || null;
  }, [
    hasPermission,
    settings,
    menuOrPosition,
    user,
    checkCondition,
    shouldHideMenuItem,
    mainDomain,
    isTenantSidebarRequest,
  ]);
}

// ===== TENANT SIDEBAR SEPARATE MENU =====
function buildTenantSidebarMenu(): MenuConfig {
  const items: MenuItemConfig[] = [
   
    {
      id: 'tenant_analytics',
      label: 'Analyticss',
      type: 'route',
      action: '/admin',
      order: 1,
    },
    {
      id: 'tenant_plans',
      label: 'Plans',
      type: 'route',
      action: '/admin/plans',
      order: 2,
    },
    {
      id: 'tenant_subscriptions',
      label: 'Subscriptions',
      type: 'route',
      action: '/admin/subscriptions',
      order: 3,
    },
    {
      id: 'tenant_manage_subscription',
      label: 'Manage Subscription',
      type: 'route',
      action: '/tenant/billing',
      order: 4,
    },
    {
      id: 'tenant_users',
      label: 'Users',
      type: 'route',
      action: '/admin/users',
      order: 5,
    },
    {
      id: 'tenant_roles',
      label: 'Roles',
      type: 'route',
      action: '/admin/roles',
      order: 6,
    },
    {
      id: 'tenant_tags',
      label: 'Tags',
      type: 'route',
      action: '/admin/tags',
      order: 7,
    },
    {
      id: 'tenant_files',
      label: 'Files',
      type: 'route',
      action: '/admin/files',
      order: 8,
    },
    {
      id: 'tenant_pages',
      label: 'Pages',
      type: 'route',
      action: '/admin/custom-pages',
      order: 9,
    },
    {
      id: 'tenant_appearance',
      label: 'Appearance',
      type: 'route',
      action: '/admin/appearance',
      order: 10,
    },
    {
      id: 'tenant_settings',
      label: 'Settings',
      type: 'route',
      action: '/admin/settings',
      order: 11,
    },
    {
      id: 'tenant_localizations',
      label: 'Localizations',
      type: 'route',
      action: '/admin/localizations',
      order: 12,
    },
    {
      id: 'tenant_ads',
      label: 'Ads',
      type: 'route',
      action: '/admin/ads',
      order: 13,
    },
    {
      id: 'tenant_logs',
      label: 'Logs',
      type: 'route',
      action: '/admin/logs',
      order: 14,
    },
  ];

  return {
    id: 'tenant-admin-sidebar',
    name: 'Tenant admin sidebar',
    items,
    positions: ['tenant-admin-sidebar'],
  };
}

// ===== ADMIN MENU HELPERS =====
function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }

  const hasSubscription = Boolean((user as any)?.subscriptions?.length);
  const currentStatus = hasSubscription ? 'subscribed' : 'unsubscribed';

  if (Array.isArray(item.subscriptionStatus)) {
    return item.subscriptionStatus.includes(currentStatus);
  }
  return item.subscriptionStatus === currentStatus;
}
